import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { TypedMemo } from '../../../common/types';
import { isPitchPhase, SongsBrewCardPhase } from './songsBrewCardPhase';
import { songsBrewMessages as messages } from './songsBrewMessages';
import { SongsBrewSendTrack } from './SongsBrewSendTrack';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole card; each part uses its own slice of it */
import styles from './SongsBrewHomeCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  phase: SongsBrewCardPhase;
  email: string | null;
  sendTrackUrl: string;
  titleId: string;
}

/** The eyebrow, the title and the lines of each phase (design: songsbrew-promotion SbCard). */
const SongsBrewCardTextComponent: React.FC<Props> = ({ phase, email, sendTrackUrl, titleId }) => {
  const intl = useIntl();
  const isPitch = isPitchPhase(phase);
  const eyebrow = isPitch ? messages.eyebrow : messages.name;

  return (
    <div className={styles.text}>
      <span className={styles.eyebrow}>
        <span className={styles.eyebrowDot} />
        {intl.formatMessage(eyebrow)}
      </span>
      {isPitch && (
        <>
          <b id={titleId} className={styles.title}>
            {intl.formatMessage(messages.title)}
          </b>
          <p className={styles.body}>{intl.formatMessage(messages.body)}</p>
          <span className={styles.fine}>
            <span className={styles.fineStrong}>{withBrandWord(intl.formatMessage(messages.homeByline))}</span>
            {email !== null && <span data-clarity-mask="true"> {intl.formatMessage(messages.sendTo, { email })}</span>}
          </span>
          {phase === 'error' && (
            <span className={styles.error} role="alert">
              <FontAwesomeIcon icon={faCircleExclamation} />
              {intl.formatMessage(messages.error)}
            </span>
          )}
        </>
      )}
      {phase === 'done' && (
        <>
          <b id={titleId} className={styles.title} role="status">
            {intl.formatMessage(messages.doneTitle)}
          </b>
          <p className={styles.body}>{intl.formatMessage(messages.doneBody)}</p>
          <SongsBrewSendTrack sendTrackUrl={sendTrackUrl} className={styles.perk} />
        </>
      )}
      {phase === 'refused' && (
        <>
          <b id={titleId} className={styles.title}>
            {intl.formatMessage(messages.refused)}
          </b>
          {email !== null && (
            <span className={styles.fine} data-clarity-mask="true">
              {email}
            </span>
          )}
        </>
      )}
      {phase === 'unsubscribed' && (
        <>
          <b id={titleId} className={styles.title}>
            {intl.formatMessage(messages.unsubscribedTitle)}
          </b>
          <p className={styles.body}>{intl.formatMessage(messages.unsubscribedBody)}</p>
        </>
      )}
    </div>
  );
};

export const SongsBrewCardText = TypedMemo(SongsBrewCardTextComponent);
